import React, { useState, useEffect, useRef } from "react";
import CollectionSection from "@/components/tab/CollectionSection";
import OpenTabsPanel from "@/components/tab/OpenTabsPanel";
import CreateCollectionDialog from "@/components/tab/CreateCollectionDialog";
import { Collection, Tab } from "@/types";
import { toast } from "sonner";
import TabsNav from "@/components/tab/TabsNav";
import { api } from "@/services/api";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import TabCard from "@/components/tab/TabCard";
import { useCollections } from "@/hooks/useCollections";
import { useAllWindowsTabs } from "@/hooks/useAllWindowsTabs";
import { cn } from "@/lib/utils";
import FloatingNav from "@/components/common/FloatingNav";

const TabsPage = () => {
  const [collections, setCollections] = useCollections();
  const windows = useAllWindowsTabs();
  const [expandedCollections, setExpandedCollections] = useState<string[]>([]);
  const [showOpenTabs, setShowOpenTabs] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab | null>(null);
  const [activeCollection, setActiveCollection] = useState<string | null>(null);
  const [isActiveTabFromOpenTabs, setIsActiveTabFromOpenTabs] = useState(false);
  const [lastOverId, setLastOverId] = useState<string | null>(null);

  // Only set initial expanded state on first load
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && collections?.length > 0) {
      setExpandedCollections([collections[0].collection_id]);
      didInit.current = true;
    }
  }, [collections]);

  const handleToggleExpand = (collectionId: string) => {
    setExpandedCollections((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleExpandAll = () => {
    setExpandedCollections(collections.map((c) => c.collection_id));
  };

  const handleCollapseAll = () => {
    setExpandedCollections([]);
  };

  const handleCreateCollection = async (name: string) => {
    try {
      const response = await api.createCollection({ name });
      const refreshed = await api.getCollections();
      setCollections(refreshed.data);
      setExpandedCollections(
        response.data && response.data.collection_id
          ? Array.from(new Set([...expandedCollections, response.data.collection_id]))
              .filter(id => id && refreshed.data.some(c => c.collection_id === id))
          : expandedCollections
      );
      toast.success(
        response.data && response.data.collection_id
          ? `"${name}" collection has been created successfully.`
          : "Collection created."
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create collection');
    }
  };

  const handleUpdateCollection = (updatedCollection: Collection) => {
    setCollections(
      collections.map(collection => 
        collection.collection_id === updatedCollection.collection_id ? updatedCollection : collection
      )
    );
  };

  const parseTabId = (id: string) => {
    const [type, collectionId, tabId] = id.split('-');
    return { type, collectionId, tabId };
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeData = active.data.current;
    const { type, collectionId } = parseTabId(active.id as string);
    
    if (activeData?.type === 'tab') {
      setActiveTab(activeData.tab);
      setActiveCollection(collectionId);
      setIsActiveTabFromOpenTabs(type === 'opentab');
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (event.over) {
      setLastOverId(event.over.id as string);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    let { active, over } = event;
    console.log('DragEnd:', { active, over });

    if (!over && lastOverId) {
      over = { id: lastOverId } as any;
    }

    if (!over) {
      setActiveTab(null);
      setActiveCollection(null);
      setIsActiveTabFromOpenTabs(false);
      setLastOverId(null);
      return;
    }

    const { type: activeType, collectionId: activeCollectionId, tabId: activeTabId } = parseTabId(active.id as string);
    const overId = over.id as string;
    const isEmptyDrop = overId.startsWith('empty-drop-');
    const overCollectionId = isEmptyDrop ? overId.replace('empty-drop-', '') : undefined;

    console.log('Parsed:', { activeType, activeCollectionId, tabId: activeTabId, overId, isEmptyDrop, overCollectionId });

    if (active.id !== over.id) {
      // 1. Drop from Open Tabs to a collection
      if (isEmptyDrop && activeType === 'opentab') {
        const activeData = active.data.current;
        const tab = activeData?.tab;
        if (!tab) return;
        // If this is a ChromeTab, map fields to backend Tab payload
        const isChromeTab = tab.id && !tab.tab_id;
        const title = isChromeTab ? tab.title : tab.title;
        const url = isChromeTab ? tab.url : tab.url;
        const favicon_url = isChromeTab ? tab.favIconUrl : tab.favicon_url;
        // Calculate sequence
        const destCollection = collections.find(c => c.collection_id === overCollectionId);
        const sequence = (destCollection?.tabs?.length ?? 0) + 1;
        try {
          await api.createOrUpdateTab({
            collection_id: overCollectionId,
            title,
            url,
            favicon_url,
            sequence,
            status: 'ACTIVE',
          });
          toast.success('Tab added to collection');
          // Reload collections
          const response = await api.getCollections();
          setCollections(response.data);
          setExpandedCollections(prev => Array.from(new Set([...prev, overCollectionId])).filter(id => response.data.some(c => c.collection_id === id)));
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to add tab');
        }
      }
      // 2. Move tab between collections
      else if (isEmptyDrop && (activeType === 'tab' || activeType === 'collection')) {
        const activeData = active.data.current;
        const tab = activeData?.tab;
        if (!tab) return;
        const destCollection = collections.find(c => c.collection_id === overCollectionId);
        const sequence = (destCollection?.tabs?.length ?? 0) + 1;
        try {
          await api.createOrUpdateTab({
            tab_id: tab.tab_id,
            collection_id: overCollectionId,
            title: tab.title,
            url: tab.url,
            favicon_url: tab.favicon_url,
            sequence,
            status: 'ACTIVE',
          });
          toast.success('Tab moved to collection');
          const response = await api.getCollections();
          setCollections(response.data);
          setExpandedCollections(prev => Array.from(new Set([...prev, overCollectionId])).filter(id => response.data.some(c => c.collection_id === id)));
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to move tab');
        }
      }
      // 3. Reorder tabs within a collection
      else if (activeType === 'collection' && overId.startsWith('collection-') && activeCollectionId === parseTabId(overId).collectionId) {
        const collection = collections.find(c => c.collection_id === activeCollectionId);
        if (!collection) return;
        const oldIndex = (collection?.tabs ?? []).findIndex(tab => tab.tab_id === activeTabId);
        const newIndex = (collection?.tabs ?? []).findIndex(tab => tab.tab_id === parseTabId(overId).tabId);
        const newTabs = arrayMove(collection?.tabs ?? [], oldIndex, newIndex);
        // Update sequence for all tabs
        try {
          await Promise.all(newTabs.map((tab, idx) =>
            api.createOrUpdateTab({
              tab_id: tab.tab_id,
              collection_id: tab.collection_id,
              title: tab.title,
              url: tab.url,
              favicon_url: tab.favicon_url,
              sequence: idx + 1,
              status: tab.status,
            })
          ));
          toast.success('Tabs reordered');
          const response = await api.getCollections();
          setCollections(response.data);
          setExpandedCollections(prev => Array.from(new Set([...prev, activeCollectionId])).filter(id => response.data.some(c => c.collection_id === id)));
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to reorder tabs');
        }
      }
      // 4. Move tab to a specific position in another collection
      else {
        const activeData = active.data.current;
        const tab = activeData?.tab;
        if (!tab) return;
        const { collectionId: overCollectionId2, tabId: overTabId2 } = parseTabId(over.id as string);
        const targetCollection = collections.find(c => c.collection_id === overCollectionId2);
        if (!targetCollection) return;
        const targetIndex = (targetCollection.tabs ?? []).findIndex(t => t.tab_id === overTabId2);
        const newTabs = [...(targetCollection.tabs ?? [])];
        newTabs.splice(targetIndex >= 0 ? targetIndex : newTabs.length, 0, tab);
        // Update sequence for all tabs in target collection
        try {
          await Promise.all(newTabs.map((t, idx) =>
            api.createOrUpdateTab({
              tab_id: t.tab_id,
              collection_id: overCollectionId2,
              title: t.title,
              url: t.url,
              favicon_url: t.favicon_url,
              sequence: idx + 1,
              status: t.status,
            })
          ));
          toast.success('Tab moved and reordered');
          const response = await api.getCollections();
          setCollections(response.data);
          setExpandedCollections(prev => Array.from(new Set([...prev, overCollectionId2])).filter(id => response.data.some(c => c.collection_id === id)));
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to move and reorder tab');
        }
      }
    }

    setActiveTab(null);
    setActiveCollection(null);
    setIsActiveTabFromOpenTabs(false);
    setLastOverId(null);
  };

  const handleDeleteCollection = (collectionId: string) => {
    setCollections(collections.filter(c => c.collection_id !== collectionId));
    setExpandedCollections(prev => prev.filter(id => id !== collectionId));
    toast.success("Collection deleted successfully");
  };

  const handleDeleteTab = (collectionId: string, tabId: string) => {
    setCollections(collections.map(collection => {
      if (collection.collection_id === collectionId) {
        return {
          ...collection,
          tabs: (collection.tabs ?? []).filter(tab => tab.tab_id !== tabId)
        };
      }
      return collection;
    }));
    toast.success("Tab deleted successfully");
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      collisionDetection={closestCenter}
    >
      <div className={cn(
        "flex flex-col min-h-screen",
        "bg-background text-foreground"
      )}>
        <TabsNav 
          onCreateCollection={() => setCreateDialogOpen(true)}
          onToggleOpenTabs={() => setShowOpenTabs(!showOpenTabs)}
          showOpenTabs={showOpenTabs}
        />
        
        <div className="mt-14">
          <div className="flex-1 relative">
            <main className="h-full overflow-y-auto">
              <div className={cn(
                "px-4 md:px-6 py-4 transition-all duration-200",
                showOpenTabs ? "md:pr-[400px]" : ""
              )}>
                {collections?.filter(Boolean).map((collection) =>
                  collection && collection.collection_id ? (
                    <CollectionSection
                      key={collection.collection_id}
                      collection={collection}
                      isExpanded={expandedCollections.includes(collection.collection_id)}
                      onToggleExpand={() => handleToggleExpand(collection.collection_id)}
                      onUpdateCollection={handleUpdateCollection}
                      onDeleteCollection={handleDeleteCollection}
                      onDeleteTab={handleDeleteTab}
                    />
                  ) : null
                )}
              </div>
            </main>

            {showOpenTabs && (
              <OpenTabsPanel
                windows={windows}
                onClose={() => setShowOpenTabs(false)}
              />
            )}
          </div>

          <CreateCollectionDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onCreateCollection={handleCreateCollection}
          />

          <DragOverlay>
            {activeTab ? (
              <TabCard
                tab={activeTab}
                collectionId={activeCollection || undefined}
                isOpenTab={isActiveTabFromOpenTabs}
              />
            ) : null}
          </DragOverlay>

          <FloatingNav
            onToggleTabs={() => setShowOpenTabs(!showOpenTabs)}
            onToggleNotes={() => {}}
            showTabs={showOpenTabs}
            showNotes={false}
          />
        </div>
      </div>
    </DndContext>
  );
};

export default TabsPage; 